import api from '../api/api';

// Helper function to ensure we always throw Error objects
const handleError = (error, defaultMessage) => {
  console.error(defaultMessage, error);

  if (error.response && error.response.data) {
    // If the server returned an error object with a message
    if (error.response.data.message) {
      throw new Error(error.response.data.message);
    }
    // If the server returned a string error
    if (typeof error.response.data === 'string') {
      throw new Error(error.response.data);
    }
    // If we have error data but no message, stringify it
    throw new Error(defaultMessage);
  }

  // Network error or no response
  throw new Error(error.message || defaultMessage);
};

// Helper function to build query string that handles arrays
const buildQueryString = (params) => {
  if (!params || Object.keys(params).length === 0) return '';

  const queryParts = [];

  Object.keys(params).forEach(key => {
    const value = params[key];

    if (value === null || value === undefined) {
      return; // Skip null/undefined values
    }

    if (Array.isArray(value)) {
      // Handle arrays - repeat the key for each value
      value.forEach(item => {
        queryParts.push(`${encodeURIComponent(key)}=${encodeURIComponent(item)}`);
      });
    } else {
      // Handle single values
      queryParts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }
  });

  return queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
};

// ===== USER FUNCTIONS =====

/**
 * Get active announcements for current user's dashboard
 * @returns {Promise} Promise resolving to announcement data
 */
export const getActiveDashboardAnnouncements = async () => {
  try {
    console.log('Fetching active dashboard announcements');
    const response = await api.get('/announcements/dashboard');
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to fetch dashboard announcements');
  }
};

/**
 * Record announcement view
 * @param {string} announcementId - Announcement ID
 * @returns {Promise} Promise resolving to status data
 */
export const recordAnnouncementView = async (announcementId) => {
  try {
    console.log(`Recording view for announcement: ${announcementId}`);
    const response = await api.post(`/announcements/${announcementId}/view`);
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to record announcement view');
  }
};

/**
 * Dismiss announcement
 * @param {string} announcementId - Announcement ID
 * @returns {Promise} Promise resolving to status data
 */
export const dismissAnnouncement = async (announcementId) => {
  try {
    console.log(`Dismissing announcement: ${announcementId}`);
    const response = await api.post(`/announcements/${announcementId}/dismiss`);
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to dismiss announcement');
  }
};

/**
 * Acknowledge announcement
 * @param {string} announcementId - Announcement ID
 * @returns {Promise} Promise resolving to status data
 */
export const acknowledgeAnnouncement = async (announcementId) => {
  try {
    console.log(`Acknowledging announcement: ${announcementId}`);
    const response = await api.post(`/announcements/${announcementId}/acknowledge`);
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to acknowledge announcement');
  }
};

// ===== SUPER-ADMIN FUNCTIONS =====

/**
 * Get all announcements (super-admin)
 * @param {Object} filters - Optional filters (status, type)
 * @returns {Promise} Promise resolving to announcements list
 */
export const getAllAnnouncements = async (filters = {}) => {
  try {
    console.log('Fetching all announcements with filters:', filters);
    const queryString = buildQueryString(filters);
    const url = `/superadmin/announcements${queryString}`;
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to fetch announcements');
  }
};

/**
 * Get single announcement by ID (super-admin)
 * @param {string} id - Announcement ID
 * @returns {Promise} Promise resolving to announcement data
 */
export const getAnnouncementById = async (id) => {
  try {
    console.log(`Fetching announcement: ${id}`);
    const response = await api.get(`/superadmin/announcements/${id}`);
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to fetch announcement');
  }
};

/**
 * Create new announcement (super-admin)
 * @param {Object} announcementData - Announcement data
 * @returns {Promise} Promise resolving to created announcement
 */
export const createAnnouncement = async (announcementData) => {
  try {
    console.log('Creating announcement:', announcementData);
    const response = await api.post('/superadmin/announcements', announcementData);
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to create announcement');
  }
};

/**
 * Update announcement (super-admin)
 * @param {string} id - Announcement ID
 * @param {Object} announcementData - Updated announcement data
 * @returns {Promise} Promise resolving to updated announcement
 */
export const updateAnnouncement = async (id, announcementData) => {
  try {
    console.log(`Updating announcement: ${id}`, announcementData);
    const response = await api.put(`/superadmin/announcements/${id}`, announcementData);
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to update announcement');
  }
};

/**
 * Delete announcement (super-admin)
 * @param {string} id - Announcement ID
 * @returns {Promise} Promise resolving to deleted announcement
 */
export const deleteAnnouncement = async (id) => {
  try {
    console.log(`Deleting announcement: ${id}`);
    const response = await api.delete(`/superadmin/announcements/${id}`);
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to delete announcement');
  }
};

/**
 * Get announcement statistics (super-admin)
 * @param {string} id - Announcement ID
 * @returns {Promise} Promise resolving to announcement stats
 */
export const getAnnouncementStats = async (id) => {
  try {
    console.log(`Fetching stats for announcement: ${id}`);
    const response = await api.get(`/superadmin/announcements/${id}/stats`);
    return response.data;
  } catch (error) {
    handleError(error, 'Failed to fetch announcement stats');
  }
};
